import { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { X, Plus, Minus, Check, Sparkles } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { LOW_STOCK } from "../lib/flash";
import { BOX_PRICE, BOX_MAX_VALUE, BOX_MIN_PRODUCTS as MIN_PRODUCTS, BOX_MIN_VALUE, euro } from "../lib/box";
import { BoxInfoAccordion } from "./BoxInfoAccordion";

export const BoxComposer = ({ currentItems, onClose, onComposed, activation = false, onActivate }) => {
  const [products, setProducts] = useState([]);
  const [qty, setQty] = useState({});
  const [saving, setSaving] = useState(false);
  const [frequency, setFrequency] = useState("monthly");

  useEffect(() => {
    document.body.style.overflow = "hidden";
    api.get("/products").then((r) => setProducts(r.data.filter((p) => p.available !== false && (!activation || p.recurring))));
    const init = {};
    (currentItems || []).forEach((it) => { init[it.id] = it.qty; });
    setQty(init);
    return () => { document.body.style.overflow = ""; };
  }, [currentItems, activation]);

  const total = useMemo(
    () => products.reduce((s, p) => s + (qty[p.id] || 0) * p.price, 0),
    [products, qty]
  );
  const distinctCount = Object.values(qty).filter((q) => q > 0).length;
  const over = total > BOX_MAX_VALUE + 1e-9;
  const tooFew = distinctCount < MIN_PRODUCTS;
  const underMin = !over && !tooFew && total < BOX_MIN_VALUE - 1e-9;
  const fillMore = !over && !tooFew && !underMin && total < BOX_PRICE;
  const savings = !over && !tooFew && total > BOX_PRICE ? total - BOX_PRICE : 0;

  const setItem = (id, delta, max) => setQty((q) => {
    let next = Math.max(0, (q[id] || 0) + delta);
    if (max != null && next > max) { toast("Scorta insufficiente per questo prodotto"); next = max; }
    if (delta > 0) {
      const p = products.find((x) => x.id === id);
      const prospective = total + (p ? p.price : 0);
      if (prospective > BOX_MAX_VALUE + 1e-9) {
        toast(`Il Box Mensile arriva fino a ${euro(BOX_MAX_VALUE)} di prodotti`);
        return q;
      }
    }
    return { ...q, [id]: next };
  });

  const submit = async () => {
    const chosen = Object.entries(qty).filter(([, q]) => q > 0).map(([id, q]) => ({ id, qty: q }));
    if (chosen.length === 0) { toast.error("Seleziona almeno un prodotto"); return; }
    if (tooFew) { toast.error(`Il Box richiede almeno ${MIN_PRODUCTS} prodotti diversi`); return; }
    if (underMin) { toast.error(`Il valore complessivo dei prodotti nel carrello per la Box è ancora inferiore ai ${euro(BOX_MIN_VALUE)} minimi consentiti per accedere al pagamento, che ha la quota fissa di ${euro(BOX_PRICE)}. Aggiungi altri prodotti per procedere.`); return; }
    if (activation) {
      const items = chosen.map((c) => {
        const p = products.find((x) => x.id === c.id);
        return { id: c.id, name: p.name, price: p.price, qty: c.qty };
      });
      onActivate(items, frequency);
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.post("/subscription/compose", { items: chosen });
      toast.success(`Box del mese salvato! Valore ${euro(data.value)} — paghi sempre ${euro(data.total)}.`);
      onComposed(data);
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Salvataggio non riuscito");
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-ink/40 backdrop-blur-sm" onClick={onClose}>
      <motion.div
        initial={{ x: "100%" }} animate={{ x: 0 }} transition={{ type: "spring", damping: 30, stiffness: 300 }}
        onClick={(e) => e.stopPropagation()}
        className="safe-pt safe-pb flex h-full w-full max-w-lg flex-col bg-cream"
        data-testid="box-composer"
      >
        <div className="flex items-center justify-between border-b border-ink/10 px-6 py-5">
          <div>
            <h2 className="font-serif text-2xl text-ink">{activation ? "Scegli i prodotti del tuo box" : "Componi il box del mese"}</h2>
            <p className="text-sm text-ink-soft">
              {activation
                ? `Solo essenziali ricorrenti · min ${MIN_PRODUCTS} prodotti, fino a ${euro(BOX_MAX_VALUE)} di valore`
                : `Paghi sempre ${euro(BOX_PRICE)}/mese · solo prodotti disponibili ora`}
            </p>
          </div>
          <button onClick={onClose} data-testid="composer-close" className="grid h-9 w-9 place-items-center rounded-full hover:bg-ink/10"><X className="h-5 w-5" /></button>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-4">
          {products.map((p) => (
            <div key={p.id} className="flex items-center gap-3 border-b border-ink/5 py-3" data-testid={`composer-item-${p.id}`}>
              <img src={p.image} alt="" className="h-14 w-14 rounded-lg bg-white object-contain p-1" />
              <div className="min-w-0 flex-1">
                <p className="truncate font-medium text-ink">{p.name}</p>
                <p className="text-sm text-ink-soft">{euro(p.price)}</p>
                {p.stock != null && (
                  <p className={`text-xs font-semibold ${p.stock <= LOW_STOCK ? "text-amber" : "text-ink-soft"}`} data-testid={`composer-stock-${p.id}`}>
                    Scorta: {p.stock}{p.stock <= LOW_STOCK ? " · quasi finito" : ""}
                  </p>
                )}
              </div>
              <div className="flex items-center gap-3 rounded-full border border-ink/15 px-2 py-1">
                <button onClick={() => setItem(p.id, -1)} data-testid={`composer-dec-${p.id}`} className="text-ink hover:text-sage"><Minus className="h-3.5 w-3.5" /></button>
                <span className="w-5 text-center text-sm font-medium" data-testid={`composer-qty-${p.id}`}>{qty[p.id] || 0}</span>
                <button onClick={() => setItem(p.id, 1, p.stock)} data-testid={`composer-inc-${p.id}`} className="text-ink hover:text-sage"><Plus className="h-3.5 w-3.5" /></button>
              </div>
            </div>
          ))}
        </div>

        <div className="border-t border-ink/10 px-6 py-5">
          <div className="mb-2 flex justify-between text-sm">
            <span className="text-ink-soft">Prezzo fisso del box</span>
            <span className="font-semibold text-ink" data-testid="composer-total">
              {euro(BOX_PRICE)}<span className="text-xs font-normal text-ink-soft">{activation ? (frequency === "bimonthly" ? "/2 mesi" : "/mese") : "/mese"}</span>
            </span>
          </div>
          <div className="mb-2" data-testid="composer-cap-bar">
            <div className="flex justify-between text-[11px] text-ink-soft">
              <span>Valore prodotti</span><span>{euro(total)} / {euro(BOX_MAX_VALUE)} max</span>
            </div>
            <div className="mt-1 h-1.5 w-full overflow-hidden rounded-full bg-ink/10">
              <div className={`h-full rounded-full transition-all ${over ? "bg-red-500" : "bg-electric"}`} style={{ width: `${Math.min(100, (total / BOX_MAX_VALUE) * 100)}%` }} />
            </div>
          </div>
          {over && <p className="mb-2 text-sm text-red-600" data-testid="composer-over-cap">Il Box arriva fino a {euro(BOX_MAX_VALUE)} di prodotti. Rimuovi qualcosa.</p>}
          {tooFew && (
            <p className="mb-2 text-sm text-amber" data-testid="composer-min-products-hint">
              Seleziona almeno {MIN_PRODUCTS} prodotti diversi{distinctCount > 0 ? ` (ora ${distinctCount})` : ""}
            </p>
          )}
          {underMin && (
            <p className="mb-2 text-sm font-semibold text-red-600" data-testid="composer-min-value-hint">
              Valore box {euro(total)}: sotto i {euro(BOX_MIN_VALUE)} minimi non puoi procedere al pagamento. Aggiungi altri prodotti!
            </p>
          )}
          {fillMore && (
            <p className="mb-2 flex items-center gap-1.5 text-sm text-sage" data-testid="composer-fill-hint">
              <Sparkles className="h-4 w-4" /> Paghi comunque {euro(BOX_PRICE)}: riempi il box fino a {euro(BOX_MAX_VALUE)} di valore!
            </p>
          )}
          {savings > 0 && (
            <p className="mb-2 flex items-center gap-1.5 text-sm font-medium text-electric" data-testid="composer-savings-hint">
              <Sparkles className="h-4 w-4" /> Stai risparmiando {euro(savings)} rispetto al listino
            </p>
          )}
          {activation && (
            <div className="mb-3" data-testid="frequency-selector">
              <p className="mb-1.5 text-xs font-medium uppercase tracking-wide text-ink-soft">Ricevi il box</p>
              <div className="grid grid-cols-2 gap-2">
                {[{ v: "monthly", l: "Ogni mese" }, { v: "bimonthly", l: "Ogni 2 mesi" }].map((o) => (
                  <button key={o.v} type="button" onClick={() => setFrequency(o.v)} data-testid={`frequency-${o.v}`}
                    className={`rounded-full border px-4 py-2.5 text-sm font-medium transition-colors ${frequency === o.v ? "border-electric bg-electric/15 text-ink" : "border-ink/15 text-ink-soft hover:border-ink/30"}`}>
                    {o.l}
                  </button>
                ))}
              </div>
              <p className="mt-1.5 text-xs text-ink-soft">Paghi {euro(BOX_PRICE)} a box · l'addebito arriva 3 giorni prima della partenza</p>
            </div>
          )}
          <BoxInfoAccordion />
          <button
            onClick={submit} disabled={over || tooFew || underMin || saving} data-testid="composer-submit"
            className="flex w-full items-center justify-center gap-2 rounded-full bg-ink py-3.5 text-sm font-semibold text-cream hover:bg-sage disabled:opacity-50"
          >
            <Check className="h-4 w-4" /> {activation ? "Vai al pagamento" : saving ? "Salvataggio…" : "Conferma il box"}
          </button>
        </div>
      </motion.div>
    </div>
  );
};
